import { action } from '@ember/object';
import Component from '@glimmer/component';

import type { BlockThemeConfig, DChartInterface } from 'webapp-types';
import { BlockRenderStep } from 'webapp/enums/enums';
import { copyObject } from 'webapp/utils/common';
import { getChildrenHeightSum, scaleValToAutofit } from 'webapp/utils/html';

import didInsert from 'webapp/modifiers/did-insert';
import exposeDatapoint from 'webapp/modifiers/expose-datapoint';
import willDestroy from 'webapp/modifiers/will-destroy';
import BlockContentCommonComparesComponent from 'webapp/components/block/content/common/compares';
import BlockContentCommonValueComponent from 'webapp/components/block/content/common/value';
import BlockContentNumberAndChartChartWrapperComponent from 'webapp/components/block/content/number-and-chart/chart-wrapper';

import type BlockRenderer from 'webapp/models-custom/block-renderer';
import type BlockState from 'webapp/models-custom/block-state';
import type { NumberPointAndChartOutputPair } from 'webapp/models-custom/visualization/number';

interface Args {
	model: NumberPointAndChartOutputPair;
	blockState: BlockState;
	blockRenderer: BlockRenderer;
	themeData: BlockThemeConfig.BlockParsed;
	isCompareOnRight: boolean;
	showTitle: boolean;
	scaleVertically: boolean;
	prePrepareNumberGrid: (node: HTMLElement) => void;
}

export default class BlockContentNumberAndChartComponent extends Component<Args> {
	get numberPoint() {
		return this.args.model.numberPoint;
	}

	get chartOutput() {
		return this.args.model.chartOutput;
	}

	get annotationContext(): DChartInterface.AnnotationContext {
		return this.args.blockState.annotationContext!;
	}

	get themeDataForChart(): BlockThemeConfig.BlockParsed {
		const themeDataCopy = copyObject(this.args.themeData);
		// just leave the colorIndex of the number, so the chart creates its area gradient (one per index in order);
		// a custom color is a class, which gets its gradient through customColorClassMap instead
		const colorIndexOrClass = this.numberPoint.colorIndexOrClass;
		themeDataCopy.visualizationConfig = {
			order: typeof colorIndexOrClass === 'number' ? [colorIndexOrClass] : [],
		};
		return themeDataCopy;
	}

	get customColorClassMap() {
		return this.args.blockRenderer.block.blockSetup.customColorClassMap;
	}

	get isSourceDataset() {
		return this.numberPoint.isSourceDataset;
	}

	private handleNumberSizing(node: HTMLElement) {
		const valueNode = node.getElementsByClassName('bc__value')[0] as HTMLElement;
		if (valueNode) {
			this.scaleValFitHorizontally(valueNode);
		}
		this.scaleNumbersToFitVertically(node);
	}

	private scaleValFitHorizontally(node: HTMLElement): void {
		// smaller max bcos it'll likely be max height vertically
		const maximumEm = this.args.isCompareOnRight ? 1.3 : 1.8;
		scaleValToAutofit(node, 0.5, maximumEm);
	}

	private scaleNumbersToFitVertically(node: HTMLElement): void {
		if (!this.args.scaleVertically) {
			return;
		}

		// if necessary, we downscale the whole number block

		const gridNode = node.closest<HTMLElement>('.bc_top-numbers-grid')!;
		if (!gridNode) {
			return;
		}

		// set size to 1em so we can get the correct height
		node.style.fontSize = '1em';

		// compare on right is only for one-line numbers, so grid height works as maxHeight
		// there can be discrepancy for currentHeight bcos for big font-sizes
		// the compare drops in multiple lines and the ratio is not precise anymore
		const maxHeight = this.args.isCompareOnRight ? gridNode.offsetHeight : node.offsetHeight,
			currentHeight = getChildrenHeightSum(node),
			ratio = currentHeight ? maxHeight / currentHeight : 1,
			sizeEm = Math.min(1, ratio); // don't enlarge past 1em, even if we have space

		node.style.fontSize = `${sizeEm}em`;
	}

	@action
	setupView(node: HTMLElement): void {
		const blockRenderer = this.args.blockRenderer;
		blockRenderer.registerStepHandler(BlockRenderStep.SINGLE_NUMBER, this, () => {
			this.handleNumberSizing(node);
			return Promise.resolve();
		});

		// check if grid has already been prepared (because when we have data immediately, this setupView is called before top-numbers setupView)
		// and to size numbers corectly, we need the grid already prepared
		// using next/afterRender just moves the problem down the line to chart (rendering with wrong numbers size values)
		const gridNode = node.closest<HTMLElement>('.bc_top-numbers-grid')!,
			isGridPrepared = !!gridNode?.style.width;
		if (!isGridPrepared) {
			this.args.prePrepareNumberGrid(node);
		}

		this.handleNumberSizing(node);

		// prepare functions for teardown, since they dont have access to this.args
		this.teardownFns.push(() => {
			blockRenderer.unregisterStepHandler(BlockRenderStep.SINGLE_NUMBER, this);
		});
	}

	private teardownFns: (() => void)[] = [];

	@action
	teardownView(): void {
		this.teardownFns.forEach((fn) => fn());
	}
	<template>
		<div
			class="bc__number action-target
				{{if this.chartOutput 'bc__number--has-chart'}}
				{{if @isCompareOnRight 'bc__number--right-compare'}}"
			data-dsid={{this.numberPoint.dsId}}
			{{exposeDatapoint this.numberPoint}}
			{{didInsert this.setupView}}
			{{willDestroy this.teardownView}}
		>

			{{#if @showTitle}}
				<span
					class="bc__number-title highcharts-color-{{this.numberPoint.colorIndexOrClass}}"
				>{{this.numberPoint.title}}
				</span>
			{{/if}}

			<span class="bc__number-content">

				<span class="bc__value-wrapper">
					<BlockContentCommonValueComponent
						@model={{this.numberPoint}}
						@loading={{this.numberPoint.loading}}
					/>
				</span>

				{{#if this.chartOutput}}
					<BlockContentNumberAndChartChartWrapperComponent
						@model={{this.chartOutput}}
						@blockRenderer={{@blockRenderer}}
						@themeData={{this.themeDataForChart}}
						@colorIndexOrClass={{this.numberPoint.colorIndexOrClass}}
						@customColorClassMap={{this.customColorClassMap}}
						@annotationContext={{this.annotationContext}}
					/>
				{{/if}}

				<BlockContentCommonComparesComponent
					@model={{this.numberPoint.comparePoints}}
					@showCompareText={{true}}
					@showCompareValue={{true}}
				/>

			</span>

		</div>
	</template>
}
